'use client';

import React, { useEffect, useState } from 'react';
import { useFormContext, FieldError } from 'react-hook-form';
import { useOrder } from './OrderContext';
import { DietaryData } from '@/types/manage.types';
import { Checkbox } from '../ui/checkbox';
import { AnimatePresence, motion } from 'framer-motion';
import { FormField } from '@/components/forms/FormField';

const dietaryPreferences = [
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'glutenFree', label: 'Gluten-Free' },
  { id: 'dairyFree', label: 'Dairy-Free' },
];

const allergies = [
  { id: 'nuts', label: 'Nuts' },
  { id: 'shellfish', label: 'Shellfish' },
  { id: 'eggs', label: 'Eggs' },
  { id: 'soy', label: 'Soy' },
];

const DietaryRequirements = () => {
  const {
    state: { dietaryData },
    setDietaryData,
  } = useOrder();
  const {
    register,
    getValues,
    reset,
    formState: { errors },
  } = useFormContext();
  const [hasLoadedData, setHasLoadedData] = useState<boolean>(false);
  const [hasDietaryNeeds, setHasDietaryNeeds] = useState(false);

  // * On mount: restore form values from context state
  useEffect(() => {
    if (!hasLoadedData && dietaryData) {
      reset(dietaryData);
      setHasLoadedData(true);
    }
  }, [reset, dietaryData, hasLoadedData]);

  // * Save form content when the component unmounts
  useEffect(() => {
    return () => {
      const currentData = getValues();
      setDietaryData(currentData as DietaryData);
      console.log('💾 Dietary data saved.');
    };
  }, [getValues, setDietaryData]);

  // if no dietary need, reset all values
  useEffect(() => {
    if (!hasDietaryNeeds) {
      reset();
    }
  }, [hasDietaryNeeds, reset]);

  const renderCheckboxGroup = (title: string, items: Array<{ id: string; label: string }>) => (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map(({ id, label }) => (
          <div className="flex items-center" key={id}>
            <label className="inline-flex items-center cursor-pointer">
              <input id={id} type="checkbox" {...register(id)} className="peer hidden" />
              <div
                className="w-3 h-3 flex items-center justify-center border-2 border-gray-300 rounded-full
               peer-checked:bg-orange-600 peer-checked:border-orange-600
               transition-colors duration-200">
                {/* <Check className="w-3 h-3 peer hidden peer-checked:block" /> */}
              </div>
              <span className="ml-2 text-gray-700 text-sm font-medium cursor-pointer">{label}</span>
            </label>

            {/* <label htmlFor={id} className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
              {label}
            </label> */}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Dietary Requirements</h2>
      {/* Toggle for user to indicate if they have dietary needs */}
      <p className="text-gray-900 mb-4">Do you have any dietary preferences or allergies, we need to be aware of?</p>
      <div className="mb-4 flex items-center">
        <Checkbox
          id="hasDietaryNeeds"
          checked={hasDietaryNeeds}
          onCheckedChange={(checked) => setHasDietaryNeeds(!!checked)}
        />
        <label htmlFor="hasDietaryNeeds" className="ml-2 text-gray-700">
          I have dietary preferences or allergies
        </label>
      </div>
      {hasDietaryNeeds && (
        <AnimatePresence>
          {hasDietaryNeeds ? (
            <motion.div
              initial={{ opacity: 0, y: -20 }} // start slightly above and invisible
              animate={{ opacity: 1, y: 0 }} // slide to original position and fade in
              exit={{ opacity: 0, y: -20 }} // fade out and slide up when removed
              transition={{ duration: 0.4, ease: 'easeOut' }}>
              <p className="text-gray-600 mb-6">Please check all that apply and feel free to add comments if needed.</p>

              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderCheckboxGroup('Dietary Preferences', dietaryPreferences)}
                  {renderCheckboxGroup('Allergies', allergies)}
                </div>

                <FormField
                  name="additionalComments"
                  register={register}
                  error={errors.additionalComments as FieldError | undefined}>
                  <FormField.Label>Additional Comments</FormField.Label>
                  <FormField.Description>
                    Please provide any additional information about your dietary needs
                  </FormField.Description>
                  <textarea
                    id="additionalComments"
                    rows={4}
                    {...register('additionalComments')}
                    className="w-full p-3 rounded-md border transition-colors resize-none bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border-input"
                    placeholder="Please provide any additional information about your dietary needs..."
                  />
                  <FormField.Error />
                </FormField>
              </div>
            </motion.div>
          ) : (
            <div style={{ display: 'none' }}>
              {renderCheckboxGroup('Dietary Preferences', dietaryPreferences)}
              {renderCheckboxGroup('Allergies', allergies)}
            </div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default DietaryRequirements;
